import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQueries } from "@tanstack/react-query";
import { ArrowLeft, GitCompareArrows, MapPin, Trash2, X } from "lucide-react";
import { TopHeader } from "@/components/TopHeader";
import { BottomNavigation } from "@/components/BottomNavigation";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { fetchPropertyDetail, type PropertyDetail } from "@/lib/property-details";
import { useCompare, MAX_COMPARE } from "@/lib/compare";
import { useGeolocation } from "@/lib/geo";
import { haversineKm } from "@/lib/map-search";

export const Route = createFileRoute("/compare")({
  head: () => ({
    meta: [
      { title: "Compare Properties · Landoo" },
      {
        name: "description",
        content:
          "Compare up to 4 Landoo listings side by side — images, price, property type, area, amenities, location, distance and posted date.",
      },
      { property: "og:title", content: "Compare Properties · Landoo" },
      {
        property: "og:description",
        content: "Put up to 4 listings side by side and pick the right property faster.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ComparePage,
});

const ROWS = [
  "Images",
  "Price",
  "Property Type",
  "Area",
  "Amenities",
  "Location",
  "Distance",
  "Posted Date",
] as const;

function formatPosted(iso: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function ComparePage() {
  const { ids, count, remove, clear } = useCompare();
  const { origin } = useGeolocation();
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);

  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: ["property-detail", id],
      queryFn: () => fetchPropertyDetail(id),
      staleTime: 60_000,
    })),
  });

  const loading = results.some((r) => r.isLoading);
  const items = results
    .map((r, i) => ({ id: ids[i], detail: r.data as PropertyDetail | null | undefined }))
    .filter((i) => i.detail);

  const distanceFor = (d: PropertyDetail) => {
    if (!origin || d.latitude == null || d.longitude == null) return "Enable GPS";
    return `${haversineKm(origin, { lat: d.latitude, lng: d.longitude }).toFixed(1)} km`;
  };

  const cell = (row: (typeof ROWS)[number], d: PropertyDetail) => {
    switch (row) {
      case "Images":
        return (
          <img
            src={d.images[0]}
            alt={d.title}
            loading="lazy"
            className="h-32 w-full rounded-xl object-cover sm:h-36"
          />
        );
      case "Price":
        return <span className="text-base font-bold text-coral">{d.priceLabel}</span>;
      case "Property Type":
        return <span className="text-sm text-brown-dark">{d.propertyType}</span>;
      case "Area":
        return (
          <span className="text-sm text-brown-dark">{d.areaSqft ? `${d.areaSqft} sqft` : "—"}</span>
        );
      case "Amenities":
        return d.amenities.length ? (
          <ul className="flex flex-wrap gap-1">
            {d.amenities.slice(0, 8).map((a) => (
              <li
                key={a}
                className="rounded-full bg-secondary px-2 py-0.5 text-[11px] text-brown-dark"
              >
                {a}
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        );
      case "Location":
        return (
          <span className="flex items-start gap-1 text-sm text-brown-dark">
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0">{[d.address, d.city].filter(Boolean).join(", ") || "—"}</span>
          </span>
        );
      case "Distance":
        return <span className="text-sm text-brown-dark">{distanceFor(d)}</span>;
      case "Posted Date":
        return <span className="text-sm text-brown-dark">{formatPosted(d.postedAt)}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <TopHeader />

      <main className="mx-auto w-full max-w-6xl px-3 py-4 sm:px-6">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 sm:flex sm:justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <Link
              to="/"
              aria-label="Back to map"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-border bg-card text-brown-dark hover:bg-secondary"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-bold text-brown-dark sm:text-xl">
                Compare Properties
              </h1>
              <p className="truncate text-xs text-muted-foreground">
                {count} of {MAX_COMPARE} selected
              </p>
            </div>
          </div>
          {count > 0 && (
            <button
              type="button"
              onClick={clear}
              className="flex shrink-0 items-center gap-1 rounded-xl border border-border px-3 py-2 text-xs font-semibold text-brown-dark transition hover:bg-secondary"
            >
              <Trash2 className="h-3.5 w-3.5" /> Clear All
            </button>
          )}
        </div>

        {!hydrated || (loading && items.length === 0) ? (
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-64 w-full rounded-2xl" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-border bg-card p-10 text-center">
            <GitCompareArrows className="mx-auto h-8 w-8 text-muted-foreground" />
            <h2 className="mt-3 text-base font-bold text-brown-dark">Nothing to compare yet</h2>
            <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
              Add up to {MAX_COMPARE} listings using the “Add to Compare” action on any property,
              then return here to see them side by side.
            </p>
            <Link
              to="/"
              className="mt-4 inline-block rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-brown-dark shadow-soft"
            >
              Browse the map
            </Link>
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-card">
            <table className="w-full min-w-[640px] border-collapse text-left">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 w-32 bg-card p-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground sm:w-40">
                    Property
                  </th>
                  {items.map(({ id, detail }) => (
                    <th key={id} className="min-w-[200px] border-l border-border p-3 align-top">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <StatusBadge purpose={detail!.kind} />
                          <Link
                            to="/property/$id"
                            params={{ id }}
                            className="mt-1 block truncate text-sm font-bold text-brown-dark hover:underline"
                          >
                            {detail!.title}
                          </Link>
                        </div>
                        <button
                          type="button"
                          onClick={() => remove(id)}
                          aria-label={`Remove ${detail!.title} from compare`}
                          className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-secondary text-brown-dark hover:bg-brand-soft"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row} className="border-t border-border align-top">
                    <th
                      scope="row"
                      className="sticky left-0 z-10 bg-card p-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                    >
                      {row}
                    </th>
                    {items.map(({ id, detail }) => (
                      <td key={id} className="border-l border-border p-3">
                        {cell(row, detail!)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <BottomNavigation />
    </div>
  );
}
